/**
 * 캔버스를 PNG 로 그리기 — 전체 내려받기(CanvasToolbar)와 **선택한 노드만 클립보드로**(SelectionToolbar)가 같이 쓴다.
 *
 * React Flow 공식 예제 방식: 그릴 경계에 맞춘 viewport 변환을 .react-flow__viewport 에 임시로 적용해 html-to-image 로 그린다.
 * 선택만 그릴 때는 고르지 않은 노드와 (양 끝이 모두 선택 안에 있지 않은) 연결선을 그리는 동안 숨긴다.
 */
import { getNodesBounds, getViewportForBounds, type Node } from '@xyflow/react';
import { toPng } from 'html-to-image';

/** 내보내기 한 변의 최대 픽셀 — 노드가 많아도 이 안에 맞춘다 (pixelRatio 2 → 실제 2배) */
const EXPORT_MAX = 2400;
/** 그림 둘레 여백(px) — 네 변 모두 같게 */
const EXPORT_PADDING = 48;

/**
 * 캔버스에 실제로 그려진 것의 경계(흐름 좌표).
 *
 * React Flow 의 getNodesBounds 는 노드 상자(105×56)만 센다. 그런데 노드 아래 이름·부제, 위의 실행 뱃지·건수는
 * 상자 **밖으로** 삐져나와 그려져서, 그 경계로 자르면 아래·좌우 여백이 사라지고 글자가 잘렸다(현장 2026-09-17).
 * 그래서 노드 안의 모든 요소와 연결선의 화면 위치를 모아 흐름 좌표로 되돌린다. 숨긴 요소(크기 0)는 세지 않는다.
 */
function drawnBounds(viewportEl: HTMLElement, flowEl: HTMLElement, fallback: { x: number; y: number; width: number; height: number }) {
  const m = new DOMMatrixReadOnly(getComputedStyle(viewportEl).transform);
  const zoom = m.a || 1;
  const origin = flowEl.getBoundingClientRect();
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  const els = viewportEl.querySelectorAll<Element>('.react-flow__node, .react-flow__node *, .react-flow__edge-path, .react-flow__edge path');
  els.forEach((el) => {
    if ((el as HTMLElement).closest?.('[data-export-skip]')) return;
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return;                 // 숨긴 요소
    minX = Math.min(minX, r.left); minY = Math.min(minY, r.top);
    maxX = Math.max(maxX, r.right); maxY = Math.max(maxY, r.bottom);
  });
  if (!Number.isFinite(minX)) return fallback;
  const toFlowX = (sx: number) => (sx - origin.left - m.e) / zoom;
  const toFlowY = (sy: number) => (sy - origin.top - m.f) / zoom;
  const x = Math.min(toFlowX(minX), fallback.x);
  const y = Math.min(toFlowY(minY), fallback.y);
  const right = Math.max(toFlowX(maxX), fallback.x + fallback.width);
  const bottom = Math.max(toFlowY(maxY), fallback.y + fallback.height);
  return { x, y, width: right - x, height: bottom - y };
}

export interface RenderOptions {
  /** 이 노드들만 그린다(연결선은 양 끝이 모두 들어 있는 것만). 없으면 전부 */
  onlyNodeIds?: Set<string>;
  /** 연결선 id → 양 끝 — onlyNodeIds 와 함께 쓴다 */
  edgeEnds?: Map<string, { source: string; target: string }>;
  /** 투명 배경 (기본은 캔버스 바닥색) */
  transparent?: boolean;
  /** 노드 밖 이름·부제를 어두운 글자로 — 다크 모드에서 투명 배경으로 뽑으면 밝은 글자가 흰 문서에서 보이지 않는다.
   *  노드 자체는 다크 색 그대로 둔다(현장 2026-10-01) */
  darkCaptions?: boolean;
}

export async function renderCanvasPng(nodes: Node[], opts: RenderOptions = {}): Promise<{ dataUrl: string; width: number; height: number } | null> {
  const viewportEl = document.querySelector('.react-flow__viewport') as HTMLElement | null;
  const flowEl = document.querySelector('.react-flow') as HTMLElement | null;
  const target = opts.onlyNodeIds ? nodes.filter((n) => opts.onlyNodeIds!.has(n.id)) : nodes;
  if (!viewportEl || !flowEl || target.length === 0) return null;

  // 편집 전용 장치(연결선 + 버튼 등, data-export-skip)와 — 선택만 그릴 때 — 고르지 않은 노드·연결선을 그리는 동안 숨긴다.
  // html-to-image 는 <svg> 를 cloneNode(true) 로 통째로 복사하고 안쪽을 걷지 않아(1.11.13 실측)
  // filter 옵션도 CSS 계산 스타일 복사도 SVG 내부에는 미치지 않는다 — 인라인 style 만 살아남는다.
  const hidden: Array<HTMLElement | SVGElement> = Array.from(viewportEl.querySelectorAll<HTMLElement | SVGElement>('[data-export-skip]'));
  if (opts.onlyNodeIds) {
    const keep = opts.onlyNodeIds;
    viewportEl.querySelectorAll<HTMLElement>('.react-flow__node').forEach((el) => {
      if (!keep.has(el.getAttribute('data-id') || '')) hidden.push(el);
    });
    viewportEl.querySelectorAll<SVGElement>('.react-flow__edge').forEach((el) => {
      const ends = opts.edgeEnds?.get(el.getAttribute('data-id') || '');
      if (!ends || !keep.has(ends.source) || !keep.has(ends.target)) hidden.push(el);
    });
    viewportEl.querySelectorAll<HTMLElement>('.react-flow__edgelabel-renderer > *').forEach((el) => hidden.push(el));
  }
  const prevDisplay = hidden.map((el) => el.style.display);
  hidden.forEach((el) => { el.style.display = 'none'; });

  // 화살표 끝 모양(marker)은 캔버스 바깥의 <svg><defs> 에 있어 뷰포트만 복사하면 따라오지 않는다 —
  // 그리는 동안 같은 정의를 뷰포트 안에 넣어 둔다.
  const markerDefs = document.getElementById('react-flow__arrowclosed')?.parentElement;
  let defsHost: SVGSVGElement | null = null;
  if (markerDefs) {
    defsHost = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    defsHost.setAttribute('width', '0');
    defsHost.setAttribute('height', '0');
    defsHost.setAttribute('style', 'position:absolute;width:0;height:0;overflow:hidden');
    defsHost.appendChild(markerDefs.cloneNode(true));
    viewportEl.insertBefore(defsHost, viewportEl.firstChild);
  }
  // 노드 그림자·선택/마우스 표시·편집 장치(아이콘·+)는 그림에 넣지 않는다 (index.css .of-exporting)
  flowEl.classList.add('of-exporting');
  if (opts.darkCaptions) flowEl.classList.add('of-export-dark-captions');
  try {
    const bounds = drawnBounds(viewportEl, flowEl, getNodesBounds(target));
    const pad = opts.onlyNodeIds ? 16 : EXPORT_PADDING;
    const scale = Math.min(1, EXPORT_MAX / (bounds.width + pad * 2), EXPORT_MAX / (bounds.height + pad * 2));
    const width = Math.ceil((bounds.width + pad * 2) * scale);
    const height = Math.ceil((bounds.height + pad * 2) * scale);
    // padding 인자는 픽셀이 아니라 비율이다 — 여백은 이미 width/height 에 더했으므로 0
    const vp = getViewportForBounds(bounds, width, height, 0.05, 2, 0);
    const dataUrl = await toPng(viewportEl, {
      // 다크 모드면 노드 색이 어두우므로 캔버스 바닥색을 그대로 쓴다. 투명이면 바닥 없이
      backgroundColor: opts.transparent ? undefined : (getComputedStyle(flowEl).backgroundColor || '#FFFFFF'),
      width,
      height,
      pixelRatio: 2,
      style: { width: `${width}px`, height: `${height}px`, transform: `translate(${vp.x}px, ${vp.y}px) scale(${vp.zoom})` },
    });
    return { dataUrl, width, height };
  } finally {
    hidden.forEach((el, i) => { el.style.display = prevDisplay[i]; });
    defsHost?.remove();
    flowEl.classList.remove('of-exporting');
    flowEl.classList.remove('of-export-dark-captions');
  }
}

/** PNG 를 클립보드로. 클립보드 이미지 쓰기를 못 하는 환경(보안 연결이 아닌 http 주소 등)이면 false */
export async function copyPngToClipboard(dataUrl: string): Promise<boolean> {
  const nav: any = navigator;
  const ClipboardItemCtor = (window as any).ClipboardItem;
  if (!nav.clipboard?.write || !ClipboardItemCtor) return false;
  try {
    const blob = await (await fetch(dataUrl)).blob();
    await nav.clipboard.write([new ClipboardItemCtor({ 'image/png': blob })]);
    return true;
  } catch {
    return false;
  }
}
